/**
 * useVisibilityAwareInterval — runs `callback` at `intervalMs` while the tab is
 * visible; pauses when the tab is backgrounded (cuts needless API load). Also
 * re-fires immediately on visibility-return so data is fresh when the operator
 * comes back.
 */
import { useEffect, useRef } from 'react';

export default function useVisibilityAwareInterval(callback, intervalMs, deps = []) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    let iv = null;
    const start = () => {
      if (iv) return;
      iv = setInterval(() => { cbRef.current?.(); }, intervalMs);
    };
    const stop = () => {
      if (iv) { clearInterval(iv); iv = null; }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        cbRef.current?.();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
